import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi/dist/zod-extensions";
import { z } from "zod";

import { enterpriseSchema } from "./enterprise";
import { userSchema } from "./user";
extendZodWithOpenApi(z);

export enum PersonaType {
  brand = "brand",
  copy = "copy",
  legal = "legal",
  none = "",
}

export const personaSchema = z.nativeEnum(PersonaType);

export const workflowCommentSchema = z.object({
  _id: z.string().optional(),
  comment: z.string(),
  persona: personaSchema,
  required: z.boolean().optional(),
  x: z.number().nullish().optional(),
  y: z.number().nullish().optional(),
  page_num: z.number().positive().openapi({ exclusiveMinimum: 0 }).optional(),
  timestamp: z
    .number()
    .positive()
    .openapi({
      exclusiveMinimum: 0,
    })
    .optional(),
});

export const workflowResultSchema = z.object({
  _id: z.string().optional(),
  summary: z.string(),
  personas: z.array(personaSchema),
  reviewDocuments: z
    .object({
      ticketId: z.string(),
      boardId: z.string(),
      status: z.string(),
      isDone: z.boolean(),
      pendingComments: z.array(workflowCommentSchema).default([]),
      finalComments: z.array(workflowCommentSchema),
    })
    .array(),
});

export const baseFolderSchema = z.object({
  _id: z.string().optional(),
  name: z.string(),
  enterprise: z.union([z.string(), enterpriseSchema]),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  updatedIndex: z.string().optional(),
  deletedAt: z.coerce.date().optional(),
  createdBy: z.union([z.string(), userSchema.partial()]).optional(),
  participants: z.array(z.union([z.string(), userSchema.partial()])).optional(),
  allowList: z.array(z.union([z.string(), userSchema.partial()])).optional(),
  workflowResults: z.array(workflowResultSchema).optional(),
});

export const folderSchema = baseFolderSchema.extend({
  parentFolder: z.union([z.string(), baseFolderSchema]).optional().nullable(),
  thumbnails: z.array(z.string()).default([]),
  totalFiles: z.number().optional(),
  personas: z.array(z.string()).optional(),
});

export const baseFolderTreeSchema = z.object({
  _id: z.string().optional(),
  name: z.string(),
});

type FolderWithSubfolder = z.infer<typeof baseFolderTreeSchema> & {
  folders?: FolderWithSubfolder[];
};

export const folderTreeSchema: z.ZodType<FolderWithSubfolder> =
  baseFolderTreeSchema
    .extend({
      folders: z.lazy(() => folderTreeSchema.array()),
    })
    .openapi({
      type: "array",
      items: {
        type: "object",
      },
    });

export type FolderTree = z.infer<typeof folderTreeSchema>;

export type Folder = z.infer<typeof folderSchema>;

export const breadcrumbSchema = z.object({
  _id: z.string(),
  name: z.string(),
});

export type Breadcrumb = z.infer<typeof breadcrumbSchema>;
